/**
 * PbiReportsInstanceTable - Table component for displaying PBI Report Instance records.
 *
 * This component renders a sortable, accessible table with per-row action menus
 * for editing and removing report instances. It follows the pattern established
 * by PbiReportsTable.tsx for consistent styling and behavior.
 *
 * Features:
 * - Sortable column headers with ascending/descending direction
 * - Action menu per row with Edit and Remove options
 * - Responsive column hiding on tablet breakpoints
 * - Overflow tooltips for truncated content
 * - Accessible aria-labels and data-testid attributes
 *
 * @module AdminSettings/PbiReportsInstanceTable
 */
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { PbiReportInstanceViewModel } from '@rsmus/ecp-userservice'
import { Styles } from '../../types'
import { tokens } from '../../styles/materialTheme'
import SortableHeader from '../../rsmCoreComponents/components/SortableHeader/SortableHeader'
import OverflowTooltip from '../OverFlowToolTip/OverFlowToolTip'
import ActionMenu, { ActionMenuItem } from '../Invoicing/Invoices/ActionMenu'
import { PBI_INSTANCE_COLUMNS } from '../../utils/constants/constants'

/**
 * Styles object for the instance table component.
 * Follows RSM design patterns with consistent spacing, typography, and color tokens.
 * Matches the styling approach from PbiReportsTable.tsx for consistency.
 */
const styles: Styles = {
  reportsInstanceTable: (theme) => ({
    '&.MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
      overflowX: 'hidden',

      '& .MuiTableCell-root': {
        borderBottom: 'none',
        maxWidth: '11.25rem',
      },
      '& .MuiTableCell-head': {
        padding: '0.75rem',
        fontFamily: 'Prelo-Bold, sans-serif',
        fontSize: '0.875rem',
        lineHeight: 1.15,
        textTransform: 'uppercase',
        verticalAlign: 'middle',
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word',
      },

      '& td.MuiTableCell-root': {
        fontFamily: 'Prelo-Book, sans-serif',
      },
      '& tbody .MuiTableRow-root:nth-of-type(odd)': {
        backgroundColor: tokens.colors.rsmGray.accessibility,
      },
      '& .MuiTableCell-body': {
        padding: '1rem',
        fontSize: '0.95rem',
        wordBreak: 'break-all',
        whiteSpace: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      // Action column CSS
      '& thead th.actions, & tbody td.actions': {
        width: '3.5rem',
        minWidth: '3.5rem',
        maxWidth: '3.5rem',
        textAlign: 'right',
        paddingRight: '0.5rem',
        paddingLeft: '0.5rem',
        alignItems: 'right',
      },
    },
    '.sortable-header': {
      cursor: 'pointer',
    },
    // Responsive column hiding for tablet breakpoints
    // Column order: 1=ReportDefinition, 2=DisplayName, 3=ClientId, 4=CEMRoleName,
    // 5=IsDashboard, 6=WorkspaceId, 7=ObjectId, 8=Actions
    // Hide: ClientId (3), WorkspaceId (6), ObjectId (7) on mobile/tablet
    [theme.breakpoints.down('tablet')]: {
      '& .MuiTableCell-root:nth-of-type(3), & .MuiTableCell-root:nth-of-type(6), & .MuiTableCell-root:nth-of-type(7)':
        {
          display: 'none',
        },
    },
  }),
  date: { textTransform: 'uppercase' },
}

type PbiReportInstanceRow = PbiReportInstanceViewModel & {
  reportDefinitionName?: string | null
}

/**
 * Props interface for the PbiReportsInstanceTable component.
 * Contains data, sorting state, and callback handlers for instance management.
 */
interface PbiReportsInstanceTableProps {
  /** Object containing instance data array and total row count */
  instancesData: { data: PbiReportInstanceRow[]; totalRows: number }
  /** Callback for handling sort changes */
  onSort: (columnName: string, direction: 'asc' | 'desc') => void
  /** Currently sorted column identifier */
  sortColumn: string
  /** Current sort direction */
  sortDirection: 'asc' | 'desc'
  /** Flag indicating if component is fully mounted */
  isMounted: boolean
  /** Optional callback when user selects Edit action */
  onEditInstance?: (instance: PbiReportInstanceViewModel) => void
  /** Optional callback when user selects Remove action */
  onRemoveInstance?: (instance: PbiReportInstanceViewModel) => void
}

/**
 * PbiReportsInstanceTable - Renders a table of PBI Report Instance records.
 *
 * This component displays a list of Power BI report instances with sortable columns,
 * overflow tooltips for long text, and action menus for each row. It supports
 * responsive design by hiding certain columns on smaller screens.
 *
 * @param props - Component props containing instance data and event handlers
 * @returns A React component rendering the instance table
 */
const PbiReportsInstanceTable = ({
  instancesData,
  onSort,
  sortColumn,
  sortDirection,
  isMounted,
  onEditInstance,
  onRemoveInstance,
}: PbiReportsInstanceTableProps) => {
  const { t } = useTranslation()

  /**
   * Handles sorting when a column header is clicked.
   * Toggles direction if clicking the same column, otherwise sorts ascending.
   *
   * @param columnName - The column key to sort by
   */
  const handleSort = (columnName: string) => {
    const newSortDirection: 'asc' | 'desc' =
      sortColumn === columnName && sortDirection === 'asc' ? 'desc' : 'asc'
    onSort(columnName, newSortDirection)
  }

  /**
   * Helper function to render a sortable header cell.
   * Wraps SortableHeader with TableCell for proper table structure.
   *
   * @param backendKey - The column key used for sorting (matches backend field name)
   * @param displayValue - The translated display label for the column
   * @returns A TableCell containing a SortableHeader component
   */
  const getSortableHeader = (backendKey: string, displayValue: string) => (
    <TableCell>
      <SortableHeader
        columnName={backendKey}
        currentSortColumn={sortColumn || ''}
        sortDirection={sortDirection || ''}
        isMounted={isMounted}
        handleSort={handleSort}>
        {displayValue}
      </SortableHeader>
    </TableCell>
  )

  /**
   * Generates action menu items for each row.
   * Memoized to prevent unnecessary re-renders.
   *
   * @param instanceData - The instance data for the current row
   * @returns An array of ActionMenuItem objects for Edit and Remove actions
   */
  const generateMenuItems = useCallback(
    (
      instanceData: PbiReportInstanceViewModel,
    ): ActionMenuItem<PbiReportInstanceViewModel>[] => [
      {
        label: t('PbiReportInstance.Columns.Edit'),
        handleAction: () => onEditInstance?.(instanceData),
      },
      {
        label: t('PbiReportInstance.Columns.Remove'),
        handleAction: () => onRemoveInstance?.(instanceData),
      },
    ],
    [onEditInstance, onRemoveInstance, t],
  )

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table
          id="pbiReportsInstanceTable"
          sx={styles.reportsInstanceTable}
          aria-label={t('PbiReportInstance.Title')}>
          <Box component="caption" sx={visuallyHidden}>
            {t('PbiReportInstance.Title')}
          </Box>
          <TableHead>
            <TableRow>
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.REPORT_DEFINITION_NAME,
                t('PbiReportInstance.Columns.ReportDefinition'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.DISPLAY_NAME,
                t('PbiReportInstance.Columns.DisplayName'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.CLIENT_ID,
                t('PbiReportInstance.Columns.ClientID'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.CEM_ROLE_NAME,
                t('PbiReportInstance.Columns.CEMRoleName'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.IS_DASHBOARD,
                t('PbiReportInstance.Columns.IsDashboard'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.PBI_WORKSPACE_ID,
                t('PbiReportInstance.Columns.PBIWorkspaceID'),
              )}
              {getSortableHeader(
                PBI_INSTANCE_COLUMNS.PBI_OBJECT_ID,
                t('PbiReportInstance.Columns.PBIObjectID'),
              )}
              <TableCell className="actions">
                <Box component="span" sx={visuallyHidden}>
                  {t('Common.Actions')}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {instancesData?.data.map((instanceData, index) => (
              <TableRow
                key={`instance-${instanceData.id}`}
                data-testid={`InstancesData_Row_${index}`}>
                <TableCell data-testid={`reportDefinitionName_Cell_${index}`}>
                  <OverflowTooltip id={`reportDefinitionName-tooltip-${index}`}>
                    {instanceData.reportDefinitionName ?? ''}
                  </OverflowTooltip>
                </TableCell>
                <TableCell data-testid={`displayName_Cell_${index}`}>
                  <OverflowTooltip id={`displayName-tooltip-${index}`}>
                    {instanceData.displayName}
                  </OverflowTooltip>
                </TableCell>
                <TableCell data-testid={`clientId_Cell_${index}`}>
                  <OverflowTooltip id={`clientId-tooltip-${index}`}>
                    {instanceData.clientId || '-'}
                  </OverflowTooltip>
                </TableCell>
                <TableCell data-testid={`cemRoleName_Cell_${index}`}>
                  <OverflowTooltip id={`cemRoleName-tooltip-${index}`}>
                    {instanceData.cemRoleName}
                  </OverflowTooltip>
                </TableCell>
                <TableCell data-testid={`isDashboard_Cell_${index}`}>
                  {instanceData.isDashboard
                    ? t('PowerBI.ReportModal.Yes')
                    : t('PowerBI.ReportModal.No')}
                </TableCell>
                <TableCell data-testid={`pbiWorkspaceId_Cell_${index}`}>
                  <OverflowTooltip id={`pbiWorkspaceId-tooltip-${index}`}>
                    {instanceData.pbiWorkspaceId}
                  </OverflowTooltip>
                </TableCell>
                <TableCell data-testid={`pbiObjectId_Cell_${index}`}>
                  <OverflowTooltip id={`pbiObjectId-tooltip-${index}`}>
                    {instanceData.pbiObjectId}
                  </OverflowTooltip>
                </TableCell>
                <TableCell className="actions">
                  <ActionMenu
                    actionButtonAriaControls={`instance-${instanceData.id}-menu`}
                    actionButtonDescribedBy={instanceData.id?.toString() || ''}
                    id={instanceData.id?.toString() || ''}
                    data={instanceData}
                    dataTestId={`Btn_Instance_Ellipses_${index}`}
                    errorHeader={t('PbiReportInstance.Errors.ActionError')}
                    errorMessage={t(
                      'PbiReportInstance.Errors.ActionErrorMessage',
                    )}
                    errorMessageForNotFound={t(
                      'PbiReportInstance.Errors.ActionErrorNotFound',
                    )}
                    menuItems={generateMenuItems(instanceData)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default PbiReportsInstanceTable
